import { startOfDay, subMonths } from 'date-fns';

import { Operation, StatementTransactionType } from 'common/api/autogenerated/client-area';

import { i18n } from 'common/hooks/useI18n/useI18n.hook';

import { StatementCsvData, StatementOperationsType } from './types';

export const MAX_STATEMENT_RANGE_DAYS = 366;

export enum CurrencySeparator {
  SLASH = '/',
  ARROW = '→',
}

export const operationHashMap: (
  translate: i18n['translateToText'],
) => Partial<Record<StatementTransactionType, string>> = (translate) => ({
  [StatementTransactionType.BONUS]: translate('mobile:statement:transaction:bonus'),
  [StatementTransactionType.DEPOSIT]: translate('mobile:statement:transaction:deposit'),
  [StatementTransactionType.OTC_RECEIVE]: translate('mobile:statement:transaction:exchange'),
  [StatementTransactionType.OTC_SPEND]: translate('mobile:statement:transaction:exchange'),
  [StatementTransactionType.SPOT_TRADE_RECEIVE]: translate('mobile:statement:transaction:trade'),
  [StatementTransactionType.SPOT_TRADE_SPEND]: translate('mobile:statement:transaction:trade'),
  [StatementTransactionType.WITHDRAWAL_COMPLETE]: translate('mobile:statement:transaction:withdrawal-complete'),
  [StatementTransactionType.WITHDRAWAL_DECLINE]: translate('mobile:statement:transaction:withdrawal-refund'),
  [StatementTransactionType.WITHDRAWAL_REQUEST_COMPLETE]: translate('mobile:statement:transaction:withdrawal-complete'),
  [StatementTransactionType.WITHDRAWAL_REQUEST_DECLINE]: translate('mobile:statement:transaction:withdrawal-refund'),
  [StatementTransactionType.WITHDRAWAL_REQUEST]: translate('mobile:statement:transaction:withdrawal-request'),
  [StatementTransactionType.WITHDRAWAL]: translate('mobile:statement:transaction:withdrawal'),
  [StatementTransactionType.EARN_REWARD]: translate('mobile:statement:transaction:earn-reward'),
  [StatementTransactionType.EARN_TERMINATION_FEE]: translate('mobile:statement:transaction:earn-termination-fee'),
  [StatementTransactionType.FEE]: translate('mobile:statement:transaction:fee'),
  [StatementTransactionType.DERIVATIVES_PROFIT]: translate('mobile:statement:transaction:futures'),
  [StatementTransactionType.DERIVATIVES_LOSS]: translate('mobile:statement:transaction:futures'),
  [StatementTransactionType.DERIVATIVES_TRADING_FEE]: translate('mobile:statement:transaction:futures'),
  [StatementTransactionType.DERIVATIVES_FUNDING_FEE]: translate('mobile:statement:transaction:futures'),
  [StatementTransactionType.DERIVATIVES_FUNDING_FEE_REFUND]: translate('mobile:statement:transaction:futures'),
  [StatementTransactionType.DERIVATIVES_COMPENSATION]: translate('mobile:statement:transaction:futures'),
});

export const operationDirectionsHashMap: (
  translate: i18n['translateToText'],
) => Partial<Record<StatementTransactionType, string>> = (translate) => ({
  [StatementTransactionType.OTC_RECEIVE]: translate('mobile:statement:transaction:receive'),
  [StatementTransactionType.OTC_SPEND]: translate('mobile:statement:transaction:spend'),
  [StatementTransactionType.SPOT_TRADE_RECEIVE]: translate('mobile:statement:transaction:receive'),
  [StatementTransactionType.SPOT_TRADE_SPEND]: translate('mobile:statement:transaction:spend'),
  [StatementTransactionType.DERIVATIVES_PROFIT]: translate('mobile:statement:transaction:profit'),
  [StatementTransactionType.DERIVATIVES_LOSS]: translate('mobile:statement:transaction:loss'),
  [StatementTransactionType.DERIVATIVES_TRADING_FEE]: translate('mobile:statement:transaction:trading-fee'),
  [StatementTransactionType.DERIVATIVES_FUNDING_FEE]: translate('mobile:statement:transaction:funding-fee'),
  [StatementTransactionType.DERIVATIVES_FUNDING_FEE_REFUND]: translate(
    'mobile:statement:transaction:funding-fee-refund',
  ),
  [StatementTransactionType.DERIVATIVES_COMPENSATION]: translate('mobile:statement:transaction:futures:compensation'),
});

export const currencySeparatorHashMap: Partial<Record<StatementTransactionType, CurrencySeparator>> = {
  [StatementTransactionType.SPOT_TRADE_RECEIVE]: CurrencySeparator.SLASH,
  [StatementTransactionType.SPOT_TRADE_SPEND]: CurrencySeparator.SLASH,
  [StatementTransactionType.OTC_RECEIVE]: CurrencySeparator.ARROW,
  [StatementTransactionType.OTC_SPEND]: CurrencySeparator.ARROW,
};

export const headers: (t: i18n['translate']) => Array<{ label: string; key: keyof StatementCsvData }> = (translate) => [
  {
    key: 'date',
    label: translate.toText('mobile:statement:transactions:headline:date'),
  },
  {
    key: 'originTransactionId',
    label: translate.toText('mobile:statement:transactions:headline:id'),
  },
  {
    key: 'type',
    label: translate.toText('mobile:statement:transactions:headline:operation'),
  },
  {
    key: 'direction',
    label: translate.toText('mobile:statement:transactions:headline:direction'),
  },
  {
    key: 'currency',
    label: translate.toText('mobile:statement:transactions:headline:currency'),
  },
  {
    key: 'amount',
    label: translate.toText('mobile:statement:transactions:headline:amount'),
  },
  {
    key: 'balance',
    label: translate.toText('mobile:statement:transactions:headline:balance'),
  },
  {
    key: 'feeAmount',
    label: translate.toText('mobile:statement:transactions:headline:fee'),
  },
  {
    key: 'customerComment',
    label: translate.toText('mobile:statement:transactions:headline:comment'),
  },
];

export const defaultStatementPeriod = {
  startDate: startOfDay(subMonths(new Date(), 1)),
  endDate: startOfDay(new Date()),
  key: 'period',
};

export const DEFAULT_STATEMENT_PERIOD = {
  startDate: new Date(),
  endDate: new Date(),
  key: 'period',
};

export const DEFAULT_STATEMENT_OPERATIONS = (translate: i18n['translateToText']): StatementOperationsType[] => [
  {
    label: translate('mobile:statement:operations:trades'),
    value: Operation.TRADES,
  },
  {
    label: translate('mobile:statement:operations:exchange'),
    value: Operation.EXCHANGE,
  },
  {
    label: translate('mobile:statement:operations:traansactions'),
    value: Operation.TRANSACTIONS,
  },
];

export const DEFAULT_FILTERS = (
  translate: i18n['translateToText'],
  defaultPeriod: typeof DEFAULT_STATEMENT_PERIOD = DEFAULT_STATEMENT_PERIOD,
) => ({
  period: defaultPeriod,
  operations: DEFAULT_STATEMENT_OPERATIONS(translate),
  showFullStatement: true,
});
